// =============================================================================
// src/lib/shellCounts.js - the staff shell's background reads, through the one
// TanStack Query cache (lib/queryClient.js). Round 4 #3, Oct 2026.
//
// Why: the sidebar Inbox badge counted unread portal messages while the Inbox
// page counted unified items (inbox_feed_counts), so the two numbers
// disagreed. And every screen load fired ~14 duplicate shell requests: the
// sidebar badges refetched on every route change and the bell asked the same
// tables again on its own timer.
//
// Now:
//   - Inbox counts have ONE cache entry (INBOX_COUNTS_KEY). The sidebar badge,
//     the bell and the Inbox page (inboxApi.getInboxCounts -> fetchInboxCounts)
//     all read and write it, so they show the same number from the same read.
//   - The end-of-day sweep (eod_sweep) has one entry too, so the Layout's
//     "Before you go" nudge and the card it points at use the same data.
//   - Every other shell count (insurance updates, waitlist offers, ...) is a
//     shellCountKey() entry shared by whoever asks (Layout and the bell).
//
// Each read throws on error, so callers can tell "0" from "could not load".
// Everything here is RLS-scoped (no service role) and holds counts only.
//
// Round 7 #2 (Nov 2026): ONE read feeds all of them. The shell_counts()
// function (migration 20270221100000_shell_counts, SECURITY INVOKER, so RLS
// scopes it like the reads it replaces) answers every badge the role sees in
// one jsonb. The staff shell polls that one entry (shellAllKey) once a
// minute, also in a background tab (the bell's desktop alert needs it), and
// copies each value into the entry its consumers already read
// (INBOX_COUNTS_KEY, shellCountKey(...)), so no consumer changed. Those
// entries no longer poll on their own; asked directly (first mount, Retry)
// they take the value from the shell read, and only a badge the answer lacks
// reads itself. Before the migration is applied the function is missing
// (PGRST202 / 404): the one poll then makes the per-badge reads below
// instead, so the app works before and after.
// =============================================================================
import { supabase } from "./supabaseClient";
import { queryClient } from "./queryClient";
import { OPEN_STATUSES, mineFilter } from "./tasksApi";
import { practiceDaysAgo } from "./practiceDay";
import { fetchCounts as fetchInboundSmsCounts } from "./inboundSmsApi";
import { listOpenSystemAlerts } from "./chartPrepApi";
import { fetchDriverStatus } from "./ridesApi";
import { isMissingFunction, isShellAnswer, shellValueOf } from "./shellCountsCore";

export const INBOX_COUNTS_KEY = ["shell", "inbox-counts"];
export const EOD_SWEEP_KEY = ["shell", "eod-sweep"];
export const shellCountKey = (key, ...scope) => ["shell", "count", key, ...scope];
// The one shell read, per signed-in user and practice.
export const shellAllKey = (practiceId, uid) => ["shell", "all", practiceId || null, uid || null];
export const SHELL_POLL_MS = 60 * 1000;

// ---- raw reads (used as queryFn) ---------------------------------------------
export async function readInboxCounts() {
  const { data, error } = await supabase.rpc("inbox_feed_counts", {});
  if (error) throw error;
  return data || {};
}

export async function readEodSweep() {
  const { data, error } = await supabase.rpc("eod_sweep");
  if (error) throw error;
  return data || { sections: [] };
}

// A HEAD count query -> number (throws on error, never turns it into 0).
export async function headCount(query) {
  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

// Insurance updates waiting for review: the sidebar badge and the bell row
// read this one entry (shellCountKey("insurance_updates", practiceId)).
export const readInsuranceUpdatesCount = (practiceId) => headCount(
  supabase.from("insurance_update_requests").select("id", { count: "exact", head: true })
    .eq("practice_id", practiceId).eq("status", "Pending Review"));

// ---- shared fetches ------------------------------------------------------------
// maxAgeMs: reuse a cached result at most this old instead of asking again.
// 0 always asks, but still joins a read that is already in flight, which is
// what removes the duplicate when two components refresh in the same moment.
export function fetchInboxCounts({ maxAgeMs = 0 } = {}) {
  return queryClient.fetchQuery({ queryKey: INBOX_COUNTS_KEY, queryFn: readInboxCounts, staleTime: maxAgeMs });
}

export function fetchEodSweep({ maxAgeMs = 0 } = {}) {
  return queryClient.fetchQuery({ queryKey: EOD_SWEEP_KEY, queryFn: readEodSweep, staleTime: maxAgeMs });
}

// ---- round 7 #2: the one shell read ----------------------------------------------
// ctx: { practiceId, uid, role (the users row role, as the bell's "mine"
// filter uses), providerId, tz, wants: [keys], perBadgeOnly }.

// The per-badge reads, exactly as the sidebar, the bell, the Inbound SMS
// badge, the system alert banner and the driver reporter made them before.
// Used before the migration is applied, and for a key the answer lacks.
export const SHELL_READS = {
  inbox: () => readInboxCounts(),
  insurance_updates: (ctx) => readInsuranceUpdatesCount(ctx.practiceId),
  waitlist: () => headCount(supabase.from("waitlist_offers").select("id", { count: "exact", head: true }).in("status", ["Pending", "Contacted"])),
  billing: () => headCount(supabase.from("billable_items").select("id", { count: "exact", head: true }).eq("status", "Held")),
  // Encounters still in Draft / In Progress whose visit was at least ~2 days
  // ago - aging unsigned notes. encounter_date is a practice day, so the
  // cutoff is too (round 6 #9).
  unsigned_notes: (ctx) => headCount(supabase.from("encounters").select("id", { count: "exact", head: true })
    .in("status", ["Draft", "In Progress"]).lte("encounter_date", practiceDaysAgo(ctx.tz, 2))),
  // Mine = assigned to me, or unclaimed in my role's queue; snoozed tasks
  // are hidden until they wake up.
  tasks: (ctx) => headCount(supabase.from("tasks").select("id", { count: "exact", head: true })
    .in("status", OPEN_STATUSES).or(mineFilter(ctx.uid, ctx.role))
    .or("snoozed_until.is.null,snoozed_until.lte." + new Date().toISOString())),
  ready_for_you: (ctx) => headCount(supabase.from("queue_entries").select("id", { count: "exact", head: true })
    .eq("queue_status", "Roomed").eq("provider_id", ctx.providerId).not("ready_for_provider_at", "is", null)),
  inbound_sms: () => fetchInboundSmsCounts(),
  system_alerts: (ctx) => listOpenSystemAlerts(ctx.practiceId),
  ride_status: () => fetchDriverStatus(),
};

// The cache entry each consumer reads (unchanged shapes): the shell read
// writes its values here, and the consumers' own queries use these keys.
export function shellKeyFor(key, ctx) {
  if (key === "inbox") return INBOX_COUNTS_KEY;
  if (key === "tasks") return shellCountKey("tasks", ctx.uid, ctx.role);
  if (key === "ready_for_you") return shellCountKey("ready_for_you", ctx.providerId);
  if (key === "ride_status") return shellCountKey("ride_status", ctx.uid);
  return shellCountKey(key, ctx.practiceId);
}

// After the function was found missing, the shell stops asking for it for a
// while (a deploy before the migration would otherwise cost one 404 a
// minute); a later poll tries again, so applying the migration takes effect
// without a reload.
const MISSING_RETRY_MS = 15 * 60 * 1000;
let missingUntil = 0;

// One shell read -> { source: "rpc" | "per-badge", values: { key: value }, errors: { key: Error } }.
// Throws only when shell_counts() itself fails for a reason other than "not
// there yet" (network, 500): every badge then shows its error state.
export async function readShellCounts(ctx) {
  if (!ctx.perBadgeOnly && Date.now() >= missingUntil) {
    const { data, error } = await supabase.rpc("shell_counts");
    if (error && !isMissingFunction(error)) throw error;
    if (!error && isShellAnswer(data)) return { source: "rpc", values: data, errors: {} };
    missingUntil = Date.now() + MISSING_RETRY_MS;
  }
  const values = {};
  const errors = {};
  await Promise.all((ctx.wants || []).map(async (k) => {
    const read = SHELL_READS[k];
    if (!read) return;
    try { values[k] = await read(ctx); } catch (e) { errors[k] = e; }
  }));
  return { source: "per-badge", values, errors };
}

// The staff shell registers its context here (useShellFeed), so a badge's
// own query can take its value from the one shell read.
let feed = null;
export function setShellFeed(next) { feed = next; }
export function shellFeed() { return feed; }

// A badge's queryFn. With the shell feed on: the shell read's value (reused
// while fresh, joined while in flight); else, or when the answer has no value
// for this key, the badge's own read. A failed shell read throws here, so the
// badge shows "Couldn't load" (Retry asks the shell read again).
export async function readShellKey(key, ownRead) {
  const f = feed;
  if (!f || !f.ctx.practiceId || !f.ctx.uid) return ownRead();
  const res =await queryClient.fetchQuery({ queryKey: f.key, queryFn: () => readShellCounts(f.ctx), staleTime: SHELL_POLL_MS, retry: 0 });
  const v = shellValueOf(res, key);
  return v === undefined ? ownRead() : v;
}

// Pure helpers live in shellCountsCore.js (unit tested without a browser).
export { inboxOpenCount, eodOpenCount, zonedClock, eodNudgeText } from "./shellCountsCore";
